import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/siteTranslations';

export default function BookingForm({ defaultTreatment = '' }) {
  const { lang } = useLanguage();
  const t = translations[lang].contactPage;

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    treatment: defaultTreatment,
    description: '',
    website_url: '' // Honeypot antispam field
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot check
    if (formData.website_url) {
      // Bot detected, silently block
      setStatus('success');
      return;
    }

    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.description.trim()) {
      setStatus('error');
      setErrorMessage(lang === 'da' ? 'Udfyld venligst alle påkrævede felter.' : 'Please fill in all required fields.');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          treatment: formData.treatment || 'Ikke valgt',
          description: formData.description,
          website_url: formData.website_url
        })
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          treatment: '',
          description: '',
          website_url: ''
        });
      } else {
        setStatus('error');
        setErrorMessage(t.errorMsg);
      }
    } catch (err) {
      // If network offline or endpoint fail during mock test, show clear fallback
      setStatus('error');
      setErrorMessage(t.errorMsg);
    }
  };

  return (
    <div className="bg-white p-6 sm:p-8 rounded-2xl border border-[#E8E0D7] shadow-sm">
      <h3 className="font-serif-display text-2xl font-bold text-[#2C2825] mb-2">
        {t.title}
      </h3>
      <p className="text-sm text-[#6E6760] mb-5">
        {t.subtitle}
      </p>

      {/* Online Booking Direct Option via Planway */}
      <div className="mb-6 p-4 rounded-xl bg-[#EEF0F8] border border-[#2E3191]/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-left">
        <div className="space-y-0.5">
          <div className="inline-flex items-center gap-1.5 text-xs font-bold text-[#2E3191]">
            <i className="fa-solid fa-calendar-check" />
            <span>{lang === 'da' ? 'Direkte online booking' : 'Direct Online Booking'}</span>
          </div>
          <p className="text-xs text-[#2C2825]">
            {lang === 'da'
              ? 'Find en ledig tid i kalenderen og book med det samme via Planway.'
              : 'Choose your preferred time slot and book instantly on Planway.'}
          </p>
        </div>
        <a
          href="https://massage-with-jette.planway.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 px-4 py-2.5 rounded-lg bg-[#2E3191] hover:bg-[#1E2063] text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-all shadow-2xs"
        >
          <span>{lang === 'da' ? 'Book via Planway' : 'Book on Planway'}</span>
          <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" />
        </a>
      </div>

      <div className="relative flex py-1 items-center mb-5">
        <div className="grow border-t border-[#E8E0D7]"></div>
        <span className="shrink mx-3 text-xs text-[#6E6760] uppercase tracking-wider font-medium">
          {lang === 'da' ? 'Eller send en forespørgsel' : 'Or send an inquiry'}
        </span>
        <div className="grow border-t border-[#E8E0D7]"></div>
      </div>

      {status === 'success' ? (
        <div className="p-6 bg-[#EEF0F8] border border-[#2E3191]/30 rounded-xl text-center space-y-3 animate-in fade-in">
          <i className="fa-solid fa-circle-check text-3xl text-[#2E3191]" />
          <h4 className="font-serif-display text-xl font-semibold text-[#2C2825]">
            {lang === 'da' ? 'Tak for din henvendelse!' : 'Thank you!'}
          </h4>
          <p className="text-sm text-[#6E6760]">
            {t.successMsg}
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-2 inline-block px-4 py-2 rounded-lg bg-[#2E3191] text-white text-xs font-semibold hover:bg-[#1E2063] transition-colors"
          >
            {lang === 'da' ? 'Send en ny besked' : 'Send another message'}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* Honeypot Field - Absolutely hidden from humans */}
          <div className="sr-only absolute -left-[9999px] top-0 opacity-0 pointer-events-none" aria-hidden="true">
            <label htmlFor="website_url">Website URL</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              tabIndex="-1"
              autoComplete="off"
              value={formData.website_url}
              onChange={handleChange}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#2C2825] mb-1">
              {t.formName} <span className="text-[#A86D51]">*</span>
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g. Mette Hansen"
              className="w-full px-4 py-3 rounded-xl border border-[#E8E0D7] bg-[#FAF7F2] text-sm text-[#2C2825] focus:outline-none focus:border-[#2E3191] focus:bg-white transition-all"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#2C2825] mb-1">
                {t.formPhone} <span className="text-[#A86D51]">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="e.g. 81 98 37 07"
                className="w-full px-4 py-3 rounded-xl border border-[#E8E0D7] bg-[#FAF7F2] text-sm text-[#2C2825] focus:outline-none focus:border-[#2E3191] focus:bg-white transition-all phone-no-break"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#2C2825] mb-1">
                {t.formEmail} <span className="text-[#A86D51]">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="e.g. navn@mail.dk"
                className="w-full px-4 py-3 rounded-xl border border-[#E8E0D7] bg-[#FAF7F2] text-sm text-[#2C2825] focus:outline-none focus:border-[#2E3191] focus:bg-white transition-all email-no-break"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#2C2825] mb-1">
              {t.formTreatment}
            </label>
            <select
              name="treatment"
              value={formData.treatment}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-[#E8E0D7] bg-[#FAF7F2] text-sm text-[#2C2825] focus:outline-none focus:border-[#2E3191] focus:bg-white transition-all"
            >
              <option value="">-- {lang === 'da' ? 'Vælg varighed eller behandling' : 'Select duration or treatment'} --</option>
              <optgroup label={lang === 'da' ? 'Tidsbaserede behandlinger' : 'Time-based Sessions'}>
                <option value="60 min (700 DKK)">60 minutter (700 DKK)</option>
                <option value="75 min (860 DKK)">75 minutter (860 DKK)</option>
                <option value="90 min (1000 DKK)">90 minutter (1.000 DKK)</option>
                <option value="120 min (1300 DKK)">120 minutter (1.300 DKK)</option>
              </optgroup>
              <optgroup label={lang === 'da' ? 'Klippekort & Tilbud' : 'Packages & Offers'}>
                <option value="5 x behandlinger (Spar 15%)">5 x Behandlinger klippekort (Spar 15% - 1 år)</option>
              </optgroup>
              <optgroup label={lang === 'da' ? 'Specifikke behandlingsformer' : 'Specific Modalities'}>
                <option value="Bindevævsmassage">Bindevævsmassage / Deep Tissue Massage</option>
                <option value="Neuromuskulær massage">Neuromuskulær massage / Neuromuscular Massage</option>
                <option value="Biodynamisk Kranio-Sakral Terapi">Biodynamisk Kranio-Sakral Terapi</option>
                <option value="Gravidmassage">Gravidmassage / Pregnancy Massage</option>
                <option value="Efterfødselsmassage">Efterfødselsmassage / Post Partum Massage</option>
                <option value="Sportsmassage">Sportsmassage / Sports Massage</option>
                <option value="Velværemassage">Velværemassage / Wellness Massage</option>
                <option value="Udekørende behandling">Udekørende behandling (2300 Amager / Storkøbenhavn)</option>
              </optgroup>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#2C2825] mb-1">
              {lang === 'da' ? 'Beskrivelse / Ønsker' : 'Message / Details'} <span className="text-[#A86D51]">*</span>
            </label>
            <textarea
              name="description"
              rows="4"
              required
              value={formData.description}
              onChange={handleChange}
              placeholder={t.formMessage}
              className="w-full px-4 py-3 rounded-xl border border-[#E8E0D7] bg-[#FAF7F2] text-sm text-[#2C2825] focus:outline-none focus:border-[#2E3191] focus:bg-white transition-all"
            ></textarea>
          </div>

          {status === 'error' && (
            <div className="p-3 bg-[#F8F0EC] border border-[#A86D51]/30 rounded-lg text-xs text-[#A86D51] flex items-center gap-2">
              <i className="fa-solid fa-circle-exclamation shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full py-3.5 px-6 rounded-xl bg-[#2E3191] text-white text-base font-semibold hover:bg-[#1E2063] shadow-sm hover:shadow transition-all disabled:opacity-50 cursor-pointer"
          >
            {status === 'submitting' ? (
              <span className="flex items-center justify-center gap-2">
                <i className="fa-solid fa-spinner animate-spin" />
                {t.submitting}
              </span>
            ) : (
              t.submitBtn
            )}
          </button>
        </form>
      )}
    </div>
  );
}
